import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  type AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import type { IJodit } from 'jodit/types/types';
import { HxBaseControl } from '../../base/hx-base-control';
import type { JoditCustomButtonMap, JoditToolbarConfig } from '../jodit-editor/config/jodit-toolbar.types';
import { EMAIL_JODIT_CONFIG } from '../jodit-editor/config/jodit.config';
import { HxJoditEditor } from '../jodit-editor/jodit-editor';
import { HxVariableSidePanel } from './components/variable-side-panel';
import type { HxJoditVariable } from './models/variable.types';
import { enforceSmsCap, getSmsStats, type SmsStats } from './utils/sms-counter.util';

/**
 * HxRichTextEditor - Unified Editor Component
 *
 * Orchestrates two editing modes:
 *
 * - **HTML mode** (`format='html'`): Delegates to HxJoditEditor for rich text editing
 * - **Text mode** (`format='text'`): Uses a native textarea with SMS segment counting
 *
 * Both modes support variable insertion via a shared side panel.
 *
 * @example
 *   ```html
 *   <!-- HTML mode (default) -->
 *   <hx-rich-text-editor formControlName="content" [variables]="vars" />
 *
 *   <!-- SMS text mode -->
 *   <hx-rich-text-editor format="text" [variables]="vars" [maxSegments]="5" />
 *   ```;
 */
@Component({
  selector: 'hx-rich-text-editor',
  standalone: true,
  imports: [HxJoditEditor, HxVariableSidePanel],
  templateUrl: './rich-text-editor.html',
  styleUrl: './rich-text-editor.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: HxRichTextEditor,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: HxRichTextEditor,
      multi: true,
    },
  ],
  host: {
    '[class.hx-rich-text-editor]': 'true',
    '[class.hx-rich-text-editor--disabled]': 'disabledState()',
    '[class.hx-rich-text-editor--readonly]': 'readonly()',
    '[class.hx-rich-text-editor--text-mode]': 'format() === "text"',
    '[class.hx-rich-text-editor--html-mode]': 'format() === "html"',
  },
})
export class HxRichTextEditor extends HxBaseControl implements Validator {
  // =========================================================================
  // View children
  // =========================================================================

  private readonly _joditEditor = viewChild<HxJoditEditor>('joditEditor');
  private readonly _textInput = viewChild<ElementRef<HTMLTextAreaElement>>('textInput');

  // =========================================================================
  // Inputs — Mode
  // =========================================================================

  /** Editor format: 'html' for rich text, 'text' for plain text (SMS) */
  readonly format = input<'html' | 'text'>('html');

  /** Placeholder text when the editor is empty */
  readonly placeholder = input<string>('');

  /** Array of available variables for insertion in both modes */
  readonly variables = input<HxJoditVariable[]>([]);

  // =========================================================================
  // Inputs — HTML mode (passthrough to HxJoditEditor)
  // =========================================================================

  /** Toolbar configuration (HTML mode only) */
  readonly toolbar = input<JoditToolbarConfig>(true);
  /** Custom toolbar buttons (HTML mode only) */
  readonly customButtons = input<JoditCustomButtonMap>({});
  /** Maximum character length (HTML mode only) */
  readonly maxLength = input<number | undefined>(undefined);
  /** Minimum character length (HTML mode only) */
  readonly minLength = input<number | undefined>(undefined);
  /** Debounce time for value changes in ms (HTML mode only) */
  readonly debounceMs = input<number>(300);
  /** Enable HTML sanitization (HTML mode only) */
  readonly sanitize = input<boolean>(true);
  /** Height of the editor (both modes) */
  readonly height = input<number | string>(400);

  /** Always use EMAIL_JODIT_CONFIG for full HTML document preservation */
  protected readonly emailJoditConfig = EMAIL_JODIT_CONFIG;

  // =========================================================================
  // Inputs — Text mode (SMS)
  // =========================================================================

  /** Maximum SMS segments (text mode only, default: 10) */
  readonly maxSegments = input<number>(10);
  /** Number of visible textarea rows (text mode only) */
  readonly rows = input<number>(8);
  /** Reserved: auto-expand is not currently implemented for the native textarea */
  readonly autoExpand = input<boolean>(false);

  // =========================================================================
  // Outputs
  // =========================================================================

  /** Emitted when editor value changes (both modes) */
  readonly valueChange = output<string>();
  /** Emitted on content change with both HTML and text (HTML mode only) */
  readonly contentChanged = output<{ html: string; text: string }>();
  /** Emitted when a variable is inserted (both modes) */
  readonly variableInserted = output<HxJoditVariable>();
  /** Emitted when JODIT editor is created and ready (HTML mode only) */
  readonly editorCreated = output<IJodit>();
  /** Emitted when SMS stats change (text mode only) */
  readonly smsStatsChanged = output<SmsStats>();

  // =========================================================================
  // Internal state
  // =========================================================================

  /** Current text value (text mode) */
  protected readonly textValue = signal<string>('');
  /** Current HTML value (html mode) */
  protected readonly htmlValue = signal<string>('');
  /** Current SMS stats (text mode) */
  protected readonly smsStats = signal<SmsStats>(getSmsStats(''));

  // =========================================================================
  // Computed — SMS display helpers
  // =========================================================================

  /** Character count label for SMS bar */
  protected readonly charLabel = computed(() => {
    const stats = this.smsStats();
    const cap = stats.len <= stats.singleMax ? stats.singleMax : stats.hardCap;
    return `${stats.len} / ${cap} chars`;
  });

  /** Segment count label for SMS bar */
  protected readonly segmentLabel = computed(() => {
    const stats = this.smsStats();
    const encoding = stats.gsm ? 'GSM-7' : 'Unicode';
    return `${stats.segments} / ${stats.maxSegments} segment${stats.segments !== 1 ? 's' : ''} · ${encoding}`;
  });

  /** Whether the SMS hard cap has been reached */
  protected readonly atCap = computed(() => {
    const stats = this.smsStats();
    return stats.len >= stats.hardCap;
  });

  /** Resolved CSS height value for text mode container */
  protected readonly textEditorHeight = computed(() => {
    const h = this.height();
    return typeof h === 'number' ? `${h}px` : h;
  });

  // =========================================================================
  // Constructor
  // =========================================================================

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // =========================================================================
  // ControlValueAccessor overrides
  // =========================================================================

  protected override _setValue(value: string): void {
    const v = value || '';
    if (this.format() === 'text') {
      const capped = enforceSmsCap(v, this.maxSegments());
      this.textValue.set(capped);
      this.smsStats.set(getSmsStats(capped, this.maxSegments()));
    } else {
      this.htmlValue.set(v);
      // Forward to jodit so body-style extraction and iframe injection run.
      // Safe no-op when the editor is not yet initialised (viewChild is null).
      this._joditEditor()?.setValue(v);
    }
  }

  protected override _validate(control: AbstractControl): ValidationErrors | null {
    if (this.format() === 'text') {
      const value = (control.value as string) || '';
      if (this.required() && !value.trim()) {
        return { required: true };
      }
      return null;
    }
    // HTML mode validation is handled by HxJoditEditor internally
    return null;
  }

  // =========================================================================
  // Event handlers — HTML mode
  // =========================================================================

  /** Handle value change from HxJoditEditor */
  protected onHtmlValueChange(value: string): void {
    this.htmlValue.set(value);
    this.notifyChange(value);
    this.valueChange.emit(value);
  }

  /**
   * Handle editorCreated from HxJoditEditor.
   *
   * When `_setValue` fires before the viewChild exists (editor not yet initialised), the `setValue()` call above is a
   * no-op. This handler replays the stored `htmlValue` the moment jodit becomes ready, ensuring body styles are
   * extracted and injected.
   */
  protected onEditorCreated(joditInstance: IJodit): void {
    const v = this.htmlValue();
    if (v) {
      this._joditEditor()?.setValue(v);
    }
    this.editorCreated.emit(joditInstance);
  }

  // =========================================================================
  // Event handlers — Text mode
  // =========================================================================

  /** Handle input from textarea in text mode */
  protected onTextInput(event: Event): void {
    const capped = enforceSmsCap((event.target as HTMLTextAreaElement).value, this.maxSegments());

    this.textValue.set(capped);
    const stats = getSmsStats(capped, this.maxSegments());
    this.smsStats.set(stats);

    this.notifyChange(capped);
    this.valueChange.emit(capped);
    this.smsStatsChanged.emit(stats);
    this.markTouched();
  }

  // =========================================================================
  // Variable insertion
  // =========================================================================

  /** Handle variable selection from side panel */
  protected onVariableSelected(variable: HxJoditVariable): void {
    if (this.format() === 'html') {
      this._insertVariableHtml(variable);
    } else {
      this._insertVariableText(variable);
    }
    this.variableInserted.emit(variable);
  }

  /** Insert variable in HTML mode via jodit editor */
  private _insertVariableHtml(variable: HxJoditVariable): void {
    const editor = this._joditEditor();
    if (editor) {
      editor.insertVariable(variable.id);
    }
  }

  /** Insert variable in text mode at cursor position */
  private _insertVariableText(variable: HxJoditVariable): void {
    const variableText = `{{${variable.id}}}`;
    const textarea = this._getTextarea();

    if (textarea) {
      const start = textarea.selectionStart ?? 0;
      const end = textarea.selectionEnd ?? start;
      const currentValue = this.textValue();

      const newValue = currentValue.slice(0, start) + variableText + currentValue.slice(end);
      const capped = enforceSmsCap(newValue, this.maxSegments());

      this.textValue.set(capped);
      const stats = getSmsStats(capped, this.maxSegments());
      this.smsStats.set(stats);

      this.notifyChange(capped);
      this.valueChange.emit(capped);
      this.smsStatsChanged.emit(stats);

      // Restore cursor position after variable
      const newCursorPos = start + variableText.length;
      requestAnimationFrame(() => {
        textarea.value = capped;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      });
    } else {
      // Fallback: append to end
      const newValue = this.textValue() + variableText;
      const capped = enforceSmsCap(newValue, this.maxSegments());

      this.textValue.set(capped);
      const stats = getSmsStats(capped, this.maxSegments());
      this.smsStats.set(stats);

      this.notifyChange(capped);
      this.valueChange.emit(capped);
      this.smsStatsChanged.emit(stats);
    }
  }

  /** Get the underlying native textarea element */
  private _getTextarea(): HTMLTextAreaElement | null {
    return this._textInput()?.nativeElement ?? null;
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /** Get the current value */
  getValue(): string {
    return this.format() === 'text' ? this.textValue() : this.htmlValue();
  }

  /** Get current SMS stats (text mode only) */
  getStats(): SmsStats {
    return this.smsStats();
  }

  /** Focus the editor */
  focus(): void {
    if (this.format() === 'html') {
      this._joditEditor()?.focus();
    } else {
      this._getTextarea()?.focus();
    }
  }
}
